import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";
import { ISSUE_OPERATOR_ACTION } from "../../issue-operator-action";

/**
 * The dump JOB is failing or being skipped — a separate alarm on purpose.
 *
 * A dump that stopped arriving and a dump job that errored are different
 * failures with different fixes, and only the first is visible from the
 * bucket. Folding them into one code would mean the day a job starts failing
 * is invisible until the last good object ages past the RPO threshold, which
 * throws away the entire warning period.
 *
 * `skipped` counts. An app the agent could not read a connection secret for
 * was EXPECTED to be dumped and was not — which is a different thing from an
 * app nobody asked to dump, and the reason the wire carries the status at all.
 */
export const VIBE_APP_BACKUP_JOB_FAILING = {
  category: ISSUE_CATEGORY.OBSERVABILITY,
  message:
    "The tenant agent's most recent logical-dump attempt for this app failed or was skipped. Separate from the staleness alarm on purpose: this fires the day the job breaks, while a stale object only becomes visible once the last good dump ages out. A skip means the agent could not read the app's connection secret, so nothing was submitted at all. Cleared once an attempt succeeds or is in flight.",
  severity: "high",
  remedy:
    "Run the dump now, and read the outcome rather than the click. This records a request the tenant's own agent honours on its next poll — the app family runs on the tenant's Nomad, not on AWS Backup — and forces today's dump to be re-submitted instead of waiting for tomorrow's window. A re-run is the fastest way to tell a transient failure from a standing one: a skip that repeats means the app's connection secret is genuinely unreadable in the tenant's Vault, which no amount of re-running fixes and which points at the app's data binding rather than at the dump. The request applies to today only and lapses at midnight UTC.",
  operatorAction: ISSUE_OPERATOR_ACTION.VIBE_RUN_APP_BACKUP_NOW
} as const satisfies IssueDefinition;
